import React, { useEffect, useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { CopilotKit } from '@copilotkit/react-core';
import { Header } from '@features/header/Header';
import { Footer } from '@features/footer/Footer';
import { Sidebar } from '@features/sidebar/Sidebar';
import { useAuthStore } from 'src/store/auth';
import { useApplicationStore } from 'src/store/application';
import { useExpiredEffectiveDateStore } from 'src/store/expired-effective-date';
import { useArchivedPlansStore } from 'src/store/archived-plans';
import { useForbiddenPlansStore } from 'src/store/forbidden-plans';
import {
  ExpiredEffectiveDateModal,
  ArchivedPlansModal,
  ForbiddenPlansModal,
} from '@features/onboarding/components/shared/modal';
import { Application } from 'src/services/interfaces/application';
import { useHealthStore } from 'src/store/health';
import { useInsuranceStore } from 'src/store/insurance';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import {
  DependentSchema,
  dependentSchema,
} from '@features/onboarding/schema/onboarding';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { cn } from '@utils/index';
import '@copilotkit/react-ui/styles.css';
import { transformApplicationToOnboardingData } from '@features/onboarding/utils/transformApplicationToOnboardingData';
import { useAI } from 'src/hooks/useAI';
import { LayoutLoading } from 'src/layouts/LayoutLoading';
import { DEFAULT_DEPENDENTS_FORM } from '@features/onboarding/constants';

export const PrivateLayout: React.FC = () => {
  const { isAuthenticated, wasAuthenticated, user } = useAuthStore();
  const {
    // currentStep,
    setData: setOnboardingData,
    isLoaded,
    setCurrentStep,
  } = useOnboardingStore();
  const {
    applications,
    originalApiPlans,
    getCurrentApplication,
    isLoading: isApplicationLoading,
    signedAt,
    currentApplicationId,
    originalEffectiveDate,
  } = useApplicationStore();
  const {
    isModalOpen: isExpiredEffectiveDateModalOpen,
    checkExpiredEffectiveDate,
  } = useExpiredEffectiveDateStore();
  const { isModalOpen: isArchivedPlansModalOpen, checkArchivedPlans } =
    useArchivedPlansStore();
  const { isModalOpen: isForbiddenPlansModalOpen, checkForbiddenPlans } =
    useForbiddenPlansStore();
  const { getConditions, getIssueDetails, getIssues, issues, conditions } =
    useHealthStore();
  const {
    plans: insurancePlans,
    getFullPlans,
    getProviders,
  } = useInsuranceStore();
  const { data } = useOnboardingStore();
  const location = useLocation();

  // Инициализируем useAI для обработки хуков
  useAI();

  const form = useForm<DependentSchema>({
    // @ts-ignore
    resolver: zodResolver(dependentSchema),
    defaultValues: {
      ...data,
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      getConditions();
      getIssueDetails();
      getIssues();
      getProviders();
      getFullPlans();
      getCurrentApplication();
    }
  }, [
    isAuthenticated,
    getConditions,
    getFullPlans,
    getProviders,
    getIssueDetails,
    getIssues,
    getCurrentApplication,
  ]);

  // updateTierFromApplicationState is called in useAuthInitialization, no need to call again here
  // to avoid duplicate requests to /current

  // Check for expired effective date when application is loaded
  useEffect(() => {
    console.log('[PrivateLayout] Check expired effective date trigger:', {
      currentApplicationId,
      originalEffectiveDate,
    });
    if (currentApplicationId && originalEffectiveDate) {
      console.log('[PrivateLayout] Calling checkExpiredEffectiveDate');
      checkExpiredEffectiveDate();
    }
  }, [currentApplicationId, originalEffectiveDate, checkExpiredEffectiveDate]);

  // Check for archived plans in current application (uses originalApiPlans for reliable priceId matching)
  useEffect(() => {
    if (
      originalApiPlans.length > 0 &&
      insurancePlans.length > 0 &&
      currentApplicationId
    ) {
      checkArchivedPlans();
    }
  }, [
    originalApiPlans,
    insurancePlans,
    currentApplicationId,
    checkArchivedPlans,
  ]);

  // Check for plans forbidden in the user's state (forbiddenStates limitation)
  useEffect(() => {
    if (
      originalApiPlans.length > 0 &&
      insurancePlans.length > 0 &&
      currentApplicationId
    ) {
      checkForbiddenPlans();
    }
  }, [
    originalApiPlans,
    insurancePlans,
    currentApplicationId,
    checkForbiddenPlans,
  ]);

  useEffect(() => {
    if (applications.length > 0 && insurancePlans.length > 0 && !isLoaded) {
      const lastApplication = applications[applications.length - 1];
      if (lastApplication) {
        const transformedData = transformApplicationToOnboardingData(
          lastApplication as Application,
          insurancePlans
        );
        setOnboardingData(transformedData);

        setTimeout(() => {
          useOnboardingStore.getState().resetAllForms(transformedData);
        }, 100);
      }
      if (isLoaded && applications.length) {
        setCurrentStep(0);
      }
    }
  }, [applications, insurancePlans, setOnboardingData, isLoaded]);
  useEffect(() => {
    if (!user || !isAuthenticated) return;
    if (isApplicationLoading) return;
    if (applications.length > 0) return;
    const timeoutId = setTimeout(() => {
      const currentStoreData = useOnboardingStore.getState().data;
      const applicants = (currentStoreData?.applicants as any[]) || [];
      const primaryApplicant = applicants[0];

      const needsFirstName = !primaryApplicant?.firstName && !!user.firstName;
      const needsLastName = !primaryApplicant?.lastName && !!user.lastName;

      if (!needsFirstName && !needsLastName) return;
      if (!user.firstName && !user.lastName) return;

      const baseApplicant = {
        ...DEFAULT_DEPENDENTS_FORM,
        ...primaryApplicant,
        applicant: 'primary',
      };

      const updatedPrimary = {
        ...baseApplicant,
        firstName: baseApplicant.firstName || user.firstName || '',
        lastName: baseApplicant.lastName || user.lastName || '',
      };

      const nextApplicants = applicants.length
        ? [updatedPrimary, ...applicants.slice(1)]
        : [updatedPrimary];

      setOnboardingData({
        ...currentStoreData,
        applicants: nextApplicants as any,
      });
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [
    user?.firstName,
    user?.lastName,
    isAuthenticated,
    applications.length,
    isApplicationLoading,
    setOnboardingData,
  ]);

  useEffect(() => {
    if (data && Object.keys(data).length > 0 && form && form.reset) {
      const enhancedData = useOnboardingStore
        .getState()
        .enhanceHealthQuestionsData(data, issues, conditions);
      form.reset(enhancedData);
    }
  }, [data, form, issues, conditions]);

  if (!isAuthenticated) {
    return (
      <Navigate to={wasAuthenticated ? '/login' : '/registration'} replace />
    );
  }

  if (location.pathname === '/tester' || location.pathname === '/payment') {
    return <Outlet />;
  }
  // console.log("COPILOT KEY:", import.meta.env.VITE_APP_COPILOT_PUBLIC_KEY);
  return (
    <>
      <LayoutLoading />
      {isExpiredEffectiveDateModalOpen && <ExpiredEffectiveDateModal />}
      {isArchivedPlansModalOpen && <ArchivedPlansModal />}
      {isForbiddenPlansModalOpen && <ForbiddenPlansModal />}
      <div className="flex min-h-screen max-h-screen flex-col py-3.5 lg:pt-5 pb-6 gap-3.5 lg:gap-6 max-w-[1920px] mx-auto">
        <Header />
        <main className="flex flex-1 justify-between rounded-xl max-lg:mt-[74px] max-xl:mt-[90px] 2xl:max-h-[calc(100vh-240px)] gap-4 md:rounded-4xl pb-0 md:pb-6 2xl:pb-0 mx-6 max-lg:mx-4">
          <section
            className={cn(
              'flex flex-1 flex-col overflow-y-auto 2xl:overflow-y-hidden max-2xs:max-w-full max-lg:mb-auto max-md:overflow-y-clip mb-0'
            )}
          >
            <Outlet />
          </section>
          <Sidebar />
        </main>
        <Footer />
      </div>
    </>
  );
};
